import { StateCreator } from "zustand";
import {
  Project,
  ProjectFile,
  CreateProjectDto,
  UpdateProjectDto,
  getProjects,
  getProject,
  createProject as createProjectApi,
  updateProject as updateProjectApi,
  deleteProject as deleteProjectApi,
  getProjectFiles,
  uploadProjectFile,
  deleteProjectFile,
} from "../services/Project";

export interface ProjectsSlice {
  // State
  projects: Project[];
  selectedProject: Project | null;
  projectFiles: ProjectFile[];
  isProjectsLoading: boolean;
  projectsError: string | null;

  // Project actions
  fetchProjects: () => Promise<void>;
  fetchProject: (projectId: string) => Promise<void>;
  createProject: (data: CreateProjectDto) => Promise<Project | null>;
  updateProject: (projectId: string, data: UpdateProjectDto) => Promise<Project | null>;
  deleteProject: (projectId: string) => Promise<boolean>;
  setSelectedProject: (project: Project | null) => void;

  // File actions
  fetchProjectFiles: (projectId: string) => Promise<void>;
  addFileToProject: (
    projectId: string,
    file: File,
    onProgress?: (progress: number) => void
  ) => Promise<ProjectFile | null>;
  removeProjectFile: (projectId: string, fileId: string) => Promise<boolean>;

  // Clear error
  clearProjectsError: () => void;
}

export const createProjectsSlice: StateCreator<ProjectsSlice> = (set, get) => ({
  // Initial state
  projects: [],
  selectedProject: null,
  projectFiles: [],
  isProjectsLoading: false,
  projectsError: null,

  // Fetch all projects
  fetchProjects: async () => {
    set({ isProjectsLoading: true, projectsError: null });
    try {
      const response = await getProjects();
      if (response.success && response.data) {
        set({ projects: response.data, isProjectsLoading: false });
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to fetch projects";
        set({ projectsError: errorMsg, isProjectsLoading: false });
      }
    } catch (error) {
      set({ projectsError: "An unexpected error occurred", isProjectsLoading: false });
    }
  },

  // Fetch a single project
  fetchProject: async (projectId: string) => {
    set({ isProjectsLoading: true, projectsError: null });
    try {
      const response = await getProject(projectId);
      if (response.success && response.data) {
        set({ selectedProject: response.data, isProjectsLoading: false });
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to fetch project";
        set({ projectsError: errorMsg, isProjectsLoading: false });
      }
    } catch (error) {
      set({ projectsError: "An unexpected error occurred", isProjectsLoading: false });
    }
  },

  // Create a new project
  createProject: async (data: CreateProjectDto) => {
    set({ isProjectsLoading: true, projectsError: null });
    try {
      const response = await createProjectApi(data);
      if (response.success && response.data) {
        set((state) => ({
          projects: [response.data!, ...state.projects],
          isProjectsLoading: false,
        }));
        return response.data;
      } else {
        // Prefer the human detail (e.g. "Free trial limited to 2 projects...")
        // over the code in title (e.g. "TRIAL_PROJECT_LIMIT").
        const errorMsg =
          response.errors?.[0]?.detail ||
          response.errors?.[0]?.title ||
          "Failed to create project";
        set({ projectsError: errorMsg, isProjectsLoading: false });
        return null;
      }
    } catch (error) {
      set({ projectsError: "An unexpected error occurred", isProjectsLoading: false });
      return null;
    }
  },

  // Update a project
  updateProject: async (projectId: string, data: UpdateProjectDto) => {
    set({ isProjectsLoading: true, projectsError: null });
    try {
      const response = await updateProjectApi(projectId, data);
      if (response.success && response.data) {
        set((state) => ({
          projects: state.projects.map((p) =>
            p.id === projectId ? response.data! : p
          ),
          selectedProject:
            state.selectedProject?.id === projectId
              ? response.data!
              : state.selectedProject,
          isProjectsLoading: false,
        }));
        return response.data;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to update project";
        set({ projectsError: errorMsg, isProjectsLoading: false });
        return null;
      }
    } catch (error) {
      set({ projectsError: "An unexpected error occurred", isProjectsLoading: false });
      return null;
    }
  },

  // Delete a project
  deleteProject: async (projectId: string) => {
    set({ isProjectsLoading: true, projectsError: null });
    try {
      const response = await deleteProjectApi(projectId);
      if (response.success) {
        set((state) => ({
          projects: state.projects.filter((p) => p.id !== projectId),
          selectedProject:
            state.selectedProject?.id === projectId ? null : state.selectedProject,
          isProjectsLoading: false,
        }));
        return true;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to delete project";
        set({ projectsError: errorMsg, isProjectsLoading: false });
        return false;
      }
    } catch (error) {
      set({ projectsError: "An unexpected error occurred", isProjectsLoading: false });
      return false;
    }
  },

  // Set selected project
  setSelectedProject: (project: Project | null) => {
    set({ selectedProject: project });
  },

  // Fetch project files
  fetchProjectFiles: async (projectId: string) => {
    set({ isProjectsLoading: true, projectsError: null });
    try {
      const response = await getProjectFiles(projectId);
      if (response.success && response.data) {
        set({ projectFiles: response.data, isProjectsLoading: false });
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to fetch files";
        set({ projectsError: errorMsg, isProjectsLoading: false });
      }
    } catch (error) {
      set({ projectsError: "An unexpected error occurred", isProjectsLoading: false });
    }
  },

  // Add file to project
  addFileToProject: async (
    projectId: string,
    file: File,
    onProgress?: (progress: number) => void
  ) => {
    set({ projectsError: null });
    try {
      const response = await uploadProjectFile(projectId, file, onProgress);
      if (response.success && response.data) {
        set((state) => ({
          projectFiles: [response.data!, ...state.projectFiles],
          // Update file count in projects list
          projects: state.projects.map((p) =>
            p.id === projectId ? { ...p, fileCount: p.fileCount + 1 } : p
          ),
          selectedProject:
            state.selectedProject?.id === projectId
              ? { ...state.selectedProject, fileCount: state.selectedProject.fileCount + 1 }
              : state.selectedProject,
        }));
        return response.data;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to upload file";
        set({ projectsError: errorMsg });
        return null;
      }
    } catch (error) {
      set({ projectsError: "An unexpected error occurred" });
      return null;
    }
  },

  // Remove file from project
  removeProjectFile: async (projectId: string, fileId: string) => {
    set({ projectsError: null });
    try {
      const response = await deleteProjectFile(projectId, fileId);
      if (response.success) {
        set((state) => ({
          projectFiles: state.projectFiles.filter((f) => f.id !== fileId),
          // Update file count in projects list
          projects: state.projects.map((p) =>
            p.id === projectId ? { ...p, fileCount: Math.max(0, p.fileCount - 1) } : p
          ),
          selectedProject:
            state.selectedProject?.id === projectId
              ? {
                  ...state.selectedProject,
                  fileCount: Math.max(0, state.selectedProject.fileCount - 1),
                }
              : state.selectedProject,
        }));
        return true;
      } else {
        const errorMsg = response.errors?.[0]?.title || "Failed to delete file";
        set({ projectsError: errorMsg });
        return false;
      }
    } catch (error) {
      set({ projectsError: "An unexpected error occurred" });
      return false;
    }
  },

  // Clear error
  clearProjectsError: () => {
    set({ projectsError: null });
  },
});
